'use client';

import {
  Box,
  Card,
  CardContent,
  Container,
  lighten,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { hasRichTextContent } from '@/lib/helpers/contentHelpers';
import {
  ComponentBlocksTableWidget,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

const mapColorToTheme = (color: string | undefined, theme: Theme): string | undefined => {
  if (!color) return undefined;

  // Normalize underscores to hyphens
  const normalizedColor = color.replace(/_/g, '-');

  switch (normalizedColor) {
    case 'brand-dark':
      return theme.palette.brand.brandDark;
    case 'primary-dark':
      return theme.palette.brand.primaryDark;
    case 'primary':
      return theme.palette.brand.primary;
    case 'secondary':
      return theme.palette.brand.secondary;
    case 'purple-900':
      return theme.palette.purple[900];
    case 'purple-800':
      return theme.palette.purple[800];
    case 'purple-500':
      return theme.palette.purple[500];
    case 'purple-400':
      return theme.palette.purple[400];
    case 'purple-200':
      return theme.palette.purple[200];
    case 'purple-100':
      return theme.palette.purple[100];
    case 'gradient-1':
      return theme.palette.gradients.gradient1;
    case 'gradient-2':
      return theme.palette.gradients.gradient2;
    case 'gradient-3':
      return theme.palette.gradients.gradient3;
    case 'gradient-4':
      return theme.palette.gradients.gradient4;
    case 'gradient-cta':
      return theme.palette.gradients.gradientCta;
    case 'gradient-primary-dark':
      return theme.palette.gradients.primaryDark;
    case 'gradient-purple-dark':
      return theme.palette.gradients.purpleDark;
    case 'gradient-secondary-brand':
      return theme.palette.gradients.secondaryBrand;
    case 'orb-cover':
      return undefined;
    case 'none':
    default:
      return undefined;
  }
};

// Font mappings
const headingFontMap: Record<string, string> = {
  xs: '0.9rem',
  sm: '1rem',
  md: '1.25rem',
  lg: '1.5rem',
  xl: '1.75rem',
};

const bodyFontMap: Record<string, string> = {
  xs: '0.8rem',
  sm: '0.9rem',
  md: '1rem',
  lg: '1.125rem',
};

const parseColumnPreset = (
  preset: string | null | undefined,
  colCount: number
): string[] | null => {
  if (!preset || preset === 'auto') return null;

  // Example: c3_25_50_25
  const match = preset.match(/^c(\d+)_(.+)$/);
  if (!match) return null;

  const presetCount = Number(match[1]);
  if (presetCount !== colCount) return null;

  const nums = match[2].split('_').map((n) => Number(n));

  if (nums.length !== colCount || nums.some((n) => !Number.isFinite(n) || n <= 0)) {
    return null;
  }

  const sum = nums.reduce((a, b) => a + b, 0);
  if (sum <= 0) return null;

  return nums.map((n) => `${(n / sum) * 100}%`);
};

const normalizeCells = <T,>(
  cells: (T | null | undefined)[] | null | undefined,
  count: number
): (T | null)[] => {
  const arr = (cells ?? []).filter(Boolean) as T[];
  if (arr.length > count) return arr.slice(0, count) as (T | null)[];
  if (arr.length < count) return [...arr, ...Array(count - arr.length).fill(null)] as (T | null)[];
  return arr as (T | null)[];
};

export function TableWidget({ block }: { block: ComponentBlocksTableWidget }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { TableTitle, TableHeadings, TableRows } = block;
  const typedBlock = block as ComponentBlocksTableWidget & {
    TableCellAlignment?: string;
    TableInnerBackgroundColor?: string;
    DisableTableRowHover?: boolean;
    DisableTableRowStripe?: boolean;
  };

  const headings = (TableHeadings ?? []).filter(Boolean);
  const rows = (TableRows ?? []).filter(Boolean);
  // When there are no headers, derive column count from the max cells in any row
  const maxColsFromRows = rows.length
    ? Math.max(...rows.map((row) => (row?.Cells ?? []).filter(Boolean).length), 0)
    : 0;
  const colCount = headings.length > 0 ? headings.length : Math.max(maxColsFromRows, 1);

  const preset = block.ColumnWidthPreset ? String(block.ColumnWidthPreset) : 'auto';
  const colWidths = parseColumnPreset(preset, colCount);

  const getAlignment = (align: string | null | undefined): 'left' | 'center' | 'right' => {
    if (align === 'center' || align === 'right') return align;
    return 'left';
  };

  const hasTableSubContent = hasRichTextContent(block?.TableSubContent);

  return (
    <CustomBackground
      backgroundOptions={(block?.TableBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (block?.TableDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        sx: { 'data-component': 'TableWidget' },
      }}
    >
      {TableTitle && (
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
            {TableTitle}
          </Typography>
        </Container>
      )}

      {hasTableSubContent && <GlobalBlockRenderer content={block?.TableSubContent || []} />}

      {!isMobile && (
        <Box
          sx={{
            overflowX: 'auto',
            borderRadius: 2,
            px: { xs: 2, sm: 3, md: 4 },
            ...(typedBlock?.TableInnerBackgroundColor && {
              backgroundColor: mapColorToTheme(typedBlock?.TableInnerBackgroundColor, theme),
            }),
          }}
        >
          <Table stickyHeader sx={{ tableLayout: colWidths ? 'fixed' : 'auto' }}>
            {colWidths && (
              <colgroup>
                {colWidths.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
            )}

            {headings.length > 0 && (
              <TableHead>
                <TableRow>
                  {headings.map((heading, index) => {
                    const headingSize = heading?.FontSize ?? 'md';
                    return (
                      <TableCell
                        key={heading?.id ?? index}
                        align={getAlignment(heading?.Align)}
                        sx={{
                          backgroundColor: theme.palette.purple[900],
                          color: theme.palette.common.white,
                          py: 2,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: headingFontMap[headingSize] ?? headingFontMap.md,
                            display: 'inline-block',
                          }}
                        >
                          {heading?.Label}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {(TableRows ?? []).filter(Boolean).map((row, rowIndex) => {
                const cells = normalizeCells(row?.Cells ?? [], colCount);

                return (
                  <TableRow
                    key={row?.id ?? rowIndex}
                    sx={{
                      ...(typedBlock?.TableInnerBackgroundColor
                        ? {
                            backgroundColor: mapColorToTheme(
                              typedBlock?.TableInnerBackgroundColor,
                              theme
                            ),
                          }
                        : !typedBlock?.DisableTableRowStripe
                          ? {
                              backgroundColor:
                                rowIndex % 2 === 0
                                  ? theme.palette.common.white
                                  : theme.palette.purple[100],
                            }
                          : {
                              backgroundColor: theme.palette.common.white,
                            }),
                      ...(!typedBlock?.DisableTableRowHover && {
                        '&:hover': {
                          backgroundColor: typedBlock?.TableInnerBackgroundColor
                            ? lighten(
                                mapColorToTheme(typedBlock?.TableInnerBackgroundColor, theme) ||
                                  theme.palette.purple[100],
                                0.2
                              )
                            : lighten(theme.palette.purple[100], 0.3),
                        },
                      }),
                    }}
                  >
                    {cells.map((cell, cellIndex) => {
                      const cellSize = cell?.FontSize ?? 'md';
                      return (
                        <TableCell
                          key={cell?.id ?? cellIndex}
                          align={getAlignment(cell?.Align)}
                          sx={{
                            fontSize: bodyFontMap[cellSize] ?? bodyFontMap.md,
                            py: 1,
                            borderBottom: 0,
                            textAlign: cell?.Align ?? 'left',
                            verticalAlign: cell?.VerticalAlign ?? 'top',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: bodyFontMap[cellSize] ?? bodyFontMap.md,
                            }}
                          >
                            {cell ? <GlobalBlockRenderer content={cell?.Content || []} /> : null}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {(TableRows ?? []).filter(Boolean).map((row, rowIndex) => {
            const cells = normalizeCells(row?.Cells ?? [], colCount);

            return (
              <Card
                key={row?.id ?? rowIndex}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 2,
                  border: `1px solid ${theme.palette.purple[500]}`,
                  backgroundColor: typedBlock?.TableInnerBackgroundColor
                    ? rowIndex % 2 === 0
                      ? mapColorToTheme(typedBlock?.TableInnerBackgroundColor, theme)
                      : lighten(
                          mapColorToTheme(typedBlock?.TableInnerBackgroundColor, theme) ||
                            theme.palette.purple[500],
                          0.2
                        )
                    : !typedBlock?.DisableTableRowStripe
                      ? rowIndex % 2 === 0
                        ? theme.palette.common.white
                        : lighten(theme.palette.purple[500], 0.6)
                      : theme.palette.common.white,
                }}
              >
                <CardContent>
                  {cells.map((cell, cellIndex) => {
                    const heading = headings?.[cellIndex]?.Label ?? '';
                    return (
                      <Box key={cell?.id ?? cellIndex} sx={{ mb: 2 }}>
                        {/* Label */}
                        {heading && (
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.purple[800],
                              mb: 0.5,
                            }}
                          >
                            {heading}
                          </Typography>
                        )}

                        {/* Value */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                          }}
                        >
                          {cell ? <GlobalBlockRenderer content={cell?.Content || []} /> : null}
                        </Typography>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </CustomBackground>
  );
}
